import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, DatePicker, Modal, Select, Textarea, TextInput, useLocale, type SelectOption } from '@ministry/ui/internal';
import { NEPAL_TIME_ZONE, instantToBs, type BsDate } from '@ministry/nepali-date';

import {
  acceptAppointment,
  cancelAppointment,
  declineAppointment,
  rescheduleAppointment,
  type AppointmentDeclineReason,
  type AppointmentRow,
} from '../lib/appointments.js';
import { ApiError, NetworkError } from '../lib/api-client.js';
import { bsDateTimeToInstant } from '../lib/dates.js';
import { useT } from '../i18n/strings.js';

/**
 * SCREEN_SPECS.md I14. Three modals sharing one file (`FileActionModals.tsx`'s existing
 * pattern for I8/I9's own action set): accept-or-reschedule (one form — "accept" is simply
 * "schedule for the FIRST time"), decline, and cancel. Each takes the row's own `version`
 * as `expectedVersion` (§9.3) and surfaces `CONFLICT_HOLDER_CHANGED` / `APPOINTMENT_SLOT_TAKEN`
 * distinctly rather than a generic failure, matching `MovementActionModal`'s established
 * conflict-banner convention.
 */

function conflictBanner(
  error: unknown,
  t: (key: string, vars?: Record<string, string | number>) => string,
): React.ReactNode {
  if (error instanceof ApiError) {
    if (error.code === 'CONFLICT_HOLDER_CHANGED') {
      const details = error.details ?? {};
      const currentStatus =
        typeof (details as Record<string, unknown>).currentStatus === 'string'
          ? ((details as Record<string, unknown>).currentStatus as string)
          : t('common.notAvailable');
      return (
        <AlertBanner variant="critical" title={t('appointments.error.conflict.title')}>
          {t('appointments.error.conflict.body', { status: currentStatus })}
        </AlertBanner>
      );
    }
    if (error.code === 'APPOINTMENT_SLOT_TAKEN') {
      return <AlertBanner variant="critical" title={t('appointments.error.slotTaken.title')}>{t('appointments.error.slotTaken.body')}</AlertBanner>;
    }
    if (error.code === 'CONFLICT_STATE') {
      return <AlertBanner variant="critical" title={t('appointments.error.state.title')}>{t('appointments.error.state.body')}</AlertBanner>;
    }
    if (error.code === 'FORBIDDEN_OWNERSHIP') {
      return <AlertBanner variant="critical" title={t('appointments.error.ownership.title')}>{t('appointments.error.ownership.body')}</AlertBanner>;
    }
    if (error.retryable) return <AlertBanner variant="critical" title={t('appointments.error.network')} />;
    return <AlertBanner variant="critical" title={t('appointments.error.generic')} />;
  }
  if (error instanceof NetworkError) return <AlertBanner variant="critical" title={t('appointments.error.network')} />;
  return null;
}

export interface ScheduleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: AppointmentRow;
  /** `'accept'` when the request has never been scheduled; `'reschedule'` once it has (server treats both as the same `POST .../accept|reschedule` shape). */
  mode: 'accept' | 'reschedule';
  onDone: () => void;
  /** Parent must re-fetch the row (its `version` is now stale) before this action can be retried. */
  onStaleConflict: () => void;
}

/** `HH:mm` on the Nepal wall clock — reads the SAME way `@ministry/nepali-date` itself reads a civil date off an instant (Intl + the fixed `Asia/Kathmandu` zone), never manual +5:45 arithmetic on UTC field values (which breaks the moment minutes carry into the hour). */
const NEPAL_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: NEPAL_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function nepalTimeOf(instant: Date): string {
  const parts = NEPAL_TIME_FORMATTER.formatToParts(instant);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${hour}:${minute}`;
}

export function AppointmentScheduleModal({ open, onOpenChange, appointment, mode, onDone, onStaleConflict }: ScheduleModalProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const requestedStart = new Date(appointment.requestedStart);
  const defaultBs = instantToBs(requestedStart);
  const defaultTime = nepalTimeOf(requestedStart);

  const [bsDate, setBsDate] = React.useState<BsDate>(defaultBs);
  const [time, setTime] = React.useState(defaultTime);
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setBsDate(defaultBs);
      setTime(defaultTime);
      setReason('');
    }
    // Reset only when the modal (re)opens for this appointment, not on every render —
    // `defaultBs`/`defaultTime` are plain derived values recomputed each render, and
    // including them here would re-run this effect (and stomp in-progress edits) constantly.
  }, [open, appointment.id]);

  const timeMatch = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.exec(time);
  const timeInvalid = !timeMatch;

  const mutation = useMutation({
    mutationFn: () => {
      const [, hourStr, minuteStr] = timeMatch as RegExpExecArray;
      const scheduledStart = bsDateTimeToInstant(bsDate, Number(hourStr), Number(minuteStr)).toISOString();
      const input = {
        scheduledStart,
        expectedVersion: appointment.version,
        ...(mode === 'reschedule' && reason.trim() !== '' ? { reason: reason.trim() } : {}),
      };
      return mode === 'accept' ? acceptAppointment(appointment.id, input) : rescheduleAppointment(appointment.id, input);
    },
    onSuccess: () => {
      onOpenChange(false);
      onDone();
    },
  });

  const isStale = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_HOLDER_CHANGED';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t(mode === 'accept' ? 'appointments.accept.title' : 'appointments.reschedule.title')}
      description={t(mode === 'accept' ? 'appointments.accept.description' : 'appointments.reschedule.description')}
      confirmLabel={mutation.isPending ? t('appointments.submitting') : t(mode === 'accept' ? 'appointments.accept.submit' : 'appointments.reschedule.submit')}
      cancelLabel={t('common.cancel')}
      loading={mutation.isPending}
      confirmDisabled={timeInvalid}
      onConfirm={isStale ? undefined : () => mutation.mutate()}
    >
      <div className="flex flex-col gap-4">
        {conflictBanner(mutation.error, t)}
        {isStale ? (
          <button
            type="button"
            className="self-start text-sm font-semibold text-text-link underline"
            onClick={() => {
              onOpenChange(false);
              onStaleConflict();
            }}
          >
            {t('appointments.error.staleReload')}
          </button>
        ) : null}
        <p className="text-sm text-text-muted">
          {t('appointments.requestedFor', {
            visitor: appointment.visitor.name,
            purpose: appointment.purpose,
          })}
        </p>
        <DatePicker id="appointment-schedule-date" label={t('appointments.field.date')} value={bsDate} onChange={setBsDate} required />
        <TextInput
          id="appointment-schedule-time"
          type="time"
          label={t('appointments.field.time')}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          required
          errorText={timeInvalid ? t('appointments.field.time.invalid') : undefined}
          helperText={locale === 'ne' ? 'कार्यालय समय: आइतबार–शुक्रबार, बिहान १०:०० – साँझ ५:००' : 'Office hours: Sunday–Friday, 10:00–17:00'}
        />
        {mode === 'reschedule' ? (
          <Textarea
            id="appointment-reschedule-reason"
            label={t('appointments.field.rescheduleReason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={t('appointments.field.rescheduleReason.helper')}
          />
        ) : null}
      </div>
    </Modal>
  );
}

export interface DeclineModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: AppointmentRow;
  reasons: AppointmentDeclineReason[];
  onDone: () => void;
  onStaleConflict: () => void;
}

/**
 * A-050, surfaced rather than flattened: the reason-code select shows the CITIZEN-FACING
 * text next to each internal label, so the officer sees exactly what the visitor will be
 * told before submitting — and the one case the server refuses (`slot_taken` picked when
 * no accepted appointment actually occupies that instant) gets its own specific error
 * rather than a generic "validation failed", because the honest reason there is "you can't
 * say that, it isn't true", not "you filled the form in wrong".
 */
export function AppointmentDeclineModal({ open, onOpenChange, appointment, reasons, onDone, onStaleConflict }: DeclineModalProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [reasonCode, setReasonCode] = React.useState<string | undefined>(undefined);
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setReasonCode(undefined);
      setReason('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      declineAppointment(appointment.id, {
        reasonCode: reasonCode as string,
        reason: reason.trim(),
        expectedVersion: appointment.version,
      }),
    onSuccess: () => {
      onOpenChange(false);
      onDone();
    },
  });

  const reasonMissing = !reasonCode;
  const noteMissing = reason.trim() === '';
  const isStale = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_HOLDER_CHANGED';
  const slotNotActuallyTaken =
    mutation.error instanceof ApiError &&
    mutation.error.code === 'VALIDATION_FAILED' &&
    mutation.error.fieldErrors.some((fe) => fe.field === 'reasonCode');

  const options: SelectOption[] = reasons.map((r) => ({
    value: r.code,
    label: locale === 'ne' ? r.labelNe : r.labelEn,
  }));
  const selected = reasons.find((r) => r.code === reasonCode);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('appointments.decline.title')}
      description={t('appointments.decline.description')}
      variant="destructive"
      confirmLabel={mutation.isPending ? t('appointments.submitting') : t('appointments.decline.submit')}
      cancelLabel={t('common.cancel')}
      loading={mutation.isPending}
      confirmDisabled={reasonMissing || noteMissing}
      onConfirm={isStale ? undefined : () => mutation.mutate()}
    >
      <div className="flex flex-col gap-4">
        {slotNotActuallyTaken ? (
          <AlertBanner variant="critical" title={t('appointments.decline.error.slotNotTaken.title')}>
            {t('appointments.decline.error.slotNotTaken.body')}
          </AlertBanner>
        ) : (
          conflictBanner(mutation.error, t)
        )}
        {isStale ? (
          <button
            type="button"
            className="self-start text-sm font-semibold text-text-link underline"
            onClick={() => {
              onOpenChange(false);
              onStaleConflict();
            }}
          >
            {t('appointments.error.staleReload')}
          </button>
        ) : null}
        <Select
          id="appointment-decline-reason-code"
          label={t('appointments.decline.reasonCode')}
          placeholder={t('appointments.decline.reasonCode.placeholder')}
          options={options}
          value={reasonCode}
          onValueChange={setReasonCode}
          required
        />
        {selected ? (
          <p className="rounded-md bg-surface-muted p-3 text-sm text-text-muted">
            <span className="font-semibold text-text-primary">{t('appointments.decline.citizenSees')}</span>{' '}
            {locale === 'ne' ? selected.publicLabelNe : selected.publicLabelEn}
          </p>
        ) : null}
        <Textarea
          id="appointment-decline-reason"
          label={t('appointments.decline.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          errorText={noteMissing ? t('appointments.decline.reason.required') : undefined}
          helperText={noteMissing ? undefined : t('appointments.decline.reason.helper')}
        />
      </div>
    </Modal>
  );
}

export interface CancelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: AppointmentRow;
  onDone: () => void;
  onStaleConflict: () => void;
}

export function AppointmentCancelModal({ open, onOpenChange, appointment, onDone, onStaleConflict }: CancelModalProps): React.JSX.Element {
  const t = useT();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => cancelAppointment(appointment.id, { expectedVersion: appointment.version, reason: reason.trim() || undefined }),
    onSuccess: () => {
      onOpenChange(false);
      onDone();
    },
  });

  const isStale = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_HOLDER_CHANGED';

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('appointments.cancel.title')}
      description={t('appointments.cancel.description')}
      variant="destructive"
      confirmLabel={mutation.isPending ? t('appointments.submitting') : t('appointments.cancel.submit')}
      cancelLabel={t('common.dismiss')}
      loading={mutation.isPending}
      onConfirm={isStale ? undefined : () => mutation.mutate()}
    >
      <div className="flex flex-col gap-4">
        {conflictBanner(mutation.error, t)}
        {isStale ? (
          <button
            type="button"
            className="self-start text-sm font-semibold text-text-link underline"
            onClick={() => {
              onOpenChange(false);
              onStaleConflict();
            }}
          >
            {t('appointments.error.staleReload')}
          </button>
        ) : null}
        <Textarea id="appointment-cancel-reason" label={t('appointments.cancel.reason')} value={reason} onChange={(e) => setReason(e.target.value)} helperText={t('appointments.cancel.reason.helper')} />
      </div>
    </Modal>
  );
}
